import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import * as PIXI from "pixi.js";
import "leaflet-pixi-overlay";
import { useMap } from "react-leaflet";
import L from "leaflet";
import { useTheme } from "@mui/material";
import { percentageNormalize } from "./utils/percentageNormalized";
import { useOutletContext } from "react-router-dom";
import Supercluster from "supercluster";
import { useMapa } from "../../../../../contexts/mapaContext";

interface OutletContextType {
  config: any;
}

interface MarkerData {
  id: string;
  position: [number, number];
  iconId: string | string[];
  baseIcon: string | string[];
  tint?: string;
  label?: string;
  titulo?: string;
  tipoElemento?: string;
  cto?: string;
  onus?: string[];
  hasCto?: boolean;
  codeCity?: string[];
  alerts?: any[];
  data?: any;
  color?: string;
  coordinateIsValid?: boolean;
}

interface ClusterData {
  id: string;
  center: [number, number];
  markers?: MarkerData[];
  colorGroups?: Record<string, number>;
  isSingle: boolean;
  count?: number;
}

interface PixiOverlayProps {
  markers?: MarkerData[];
  enableClustering?: boolean;
  onClustersChange?: (clusters: ClusterData[]) => void;
  elementSelected?: any;
  elementONUSelected?: any;
  selectedCtoOnus?: any[];
  onClick: (id: string) => void;
  focusNodeId?: string | number | null;
  onBackgroundClick: () => void;
  onClusterClick: (cluster: ClusterData) => void;
  configurationMap?: any;
  showLabel?: boolean;
  rulerMode?: boolean;
}

// @ts-ignore
PIXI.settings.FAIL_IF_MAJOR_PERFORMANCE_CAVEAT = false;
// @ts-ignore
PIXI.settings.ROUND_PIXELS = true;
PIXI.utils.skipHello();
PIXI.Ticker.shared.maxFPS = 30;

const CHARS =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZ" +
  "abcdefghijklmnopqrstuvwxyz" +
  "0123456789" +
  "-_./: ";

function createBitmapFont(name: string, fill: string, size: number) {
  if (PIXI.BitmapFont.available[name]) return;

  PIXI.BitmapFont.from(
    name,
    {
      fontFamily: "Arial",
      fontSize: size,
      fontWeight: "normal",
      fill,
      //resolution: 5,
    },
    { chars: CHARS },
  );
}

const fontsizeLabel = 33;
const fontsizeCount = 20;

createBitmapFont("countFont", "#ffffff", fontsizeCount);
createBitmapFont("countFontBlack", "#000000", fontsizeCount);
createBitmapFont("labelFontWhite", "#ffffff", fontsizeLabel);
createBitmapFont("labelFontBlack", "#000000", fontsizeLabel);

const { Assets } = PIXI;

export default function PixiOverlay({
  markers = [],
  enableClustering = true,
  onClustersChange,
  elementSelected,
  elementONUSelected,
  selectedCtoOnus = [],
  onClick,
  focusNodeId,
  onBackgroundClick,
  onClusterClick,
  configurationMap,
  showLabel = false,
}: PixiOverlayProps) {
  const { config } = useOutletContext<OutletContextType>();
  const MIN_ZOOM_CLUSTER: number =
    config?.configuracoes?.ftth?.clusterFtth ?? 16;
  const maxClusterRadius: number =
    38 * ((config?.configuracoes?.ftth?.clusterArea ?? 5) / 2);
  const spriteByIdRef = useRef<Map<string, any>>(new Map());
  const explosionsRef = useRef<Map<string, any>>(new Map());
  const tickerFantasmaRef = useRef<((...args: any[]) => void) | null>(null);
  const theme = useTheme();
  const colorMode = theme.palette.mode;

  const [texturesLoaded, setTexturesLoaded] = useState(false);
  const [pixiOverlay, setPixiOverlay] = useState<any>(null);
  const [clusters, setClusters] = useState<ClusterData[]>([]);
  const [markersVisible, setMarkersVisible] = useState<MarkerData[]>([]);
  const [labelTextures, setLabelTextures] = useState<Map<string, any>>(
    new Map(),
  );

  const scRef = useRef<any>(null);
  const id2MarkerRef = useRef<Map<string, any>>(new Map());
  const { setLoadingDetails, loadingDetails } = useMapa() as any;
  const firstRenderRef = useRef(false);

  const map = useMap();

  const textureKeys = useMemo(() => {
    const baseKeys = [
      ...new Set(markers.map((m) => m.iconId).filter(Boolean)),
    ].map((key) => `marker_${key}`);

    return [...baseKeys, "cluster_icon"];
  }, [markers]);

  useEffect(() => {
    if (!enableClustering || !markers.length) {
      scRef.current = null;
      id2MarkerRef.current = new Map();
      setClusters([]);
      return;
    }

    const excludeFromClusterIds = new Set<string>();

    if (elementSelected?.id) {
      excludeFromClusterIds.add(elementSelected.id);
    }

    selectedCtoOnus.forEach((onu) => {
      if (onu?.id) {
        excludeFromClusterIds.add(onu.id);
      }
    });

    const clusterableMarkers: MarkerData[] = [];
    const nonClusterableMarkers: MarkerData[] = [];

    for (const m of markers) {
      if (!(m?.position?.[0] && m?.position?.[1])) continue;

      if (excludeFromClusterIds.has(m.id)) {
        nonClusterableMarkers.push(m);
      } else {
        clusterableMarkers.push(m);
      }
    }

    const features: any[] = [];
    const id2Marker = new Map<string, any>();

    for (const m of clusterableMarkers) {
      const [lat, lng] = m.position;
      const fid = m.id ?? `${lat},${lng},${m.iconId ?? ""}`;
      id2Marker.set(fid, m);

      features.push({
        type: "Feature",
        geometry: { type: "Point", coordinates: [lng, lat] },
        properties: {
          __id: fid,
          color: m.tint || "#ffffff",
        },
      });
    }

    id2Marker.set("__nonClusterable", nonClusterableMarkers as any);

    const sc = new Supercluster({
      radius: Math.max(1, Math.round(maxClusterRadius)),
      maxZoom: Math.max(0, Math.floor(MIN_ZOOM_CLUSTER)),
      map: (props: any) => {
        const color = props.color || "#ffffff";
        return { countByColor: { [color]: 1 }, total: 1 };
      },
      reduce: (acc: any, props: any) => {
        acc.countByColor = { ...(acc.countByColor || {}) };
        for (const [color, n] of Object.entries(props.countByColor || {})) {
          acc.countByColor[color] =
            (acc.countByColor[color] || 0) + (n as number);
        }

        acc.total = (acc.total || 0) + (props.total || 0);
      },
    });

    sc.load(features);
    scRef.current = sc;
    id2MarkerRef.current = id2Marker;
  }, [
    markers,
    enableClustering,
    maxClusterRadius,
    MIN_ZOOM_CLUSTER,
    elementSelected?.id,
    elementONUSelected?.id,
    selectedCtoOnus,
  ]);

  useEffect(() => {
    if (!textureKeys.length) return;
    setTexturesLoaded(false);

    const promises = textureKeys.map((texKey) => {
      if (Assets.cache.has(texKey)) return Promise.resolve();

      Assets.add(texKey, getDefaultIcon());

      if (texKey === "cluster_icon") {
        Assets.add(texKey, getClusterIcon());
      }
      return Assets.load(texKey);
    });

    Promise.all(promises)
      .then(() => setTexturesLoaded(true))
      .catch((err) => console.error("Falha ao carregar texturas:", err));
  }, [textureKeys]);

  const updateClusters = useCallback(() => {
    if (!map) return setClusters([]);

    const nonClusterableMarkers: MarkerData[] =
      id2MarkerRef.current.get("__nonClusterable") || [];

    if (
      !enableClustering ||
      map.getZoom() > MIN_ZOOM_CLUSTER ||
      !scRef.current
    ) {
      const allSingles: ClusterData[] = markers.map((marker) => ({
        id: marker.id,
        center: marker.position,
        markers: [marker],
        colorGroups: { [marker.tint || "#ffffff"]: 1 },
        isSingle: true,
      }));
      setClusters(allSingles);
      return;
    }

    const b = map.getBounds();
    const bbox: [number, number, number, number] = [
      b.getWest(),
      b.getSouth(),
      b.getEast(),
      b.getNorth(),
    ];
    const z = Math.max(0, Math.round(map.getZoom()));

    const raw = scRef.current.getClusters(bbox, z);

    const clustered: ClusterData[] = raw.map((f: any) => {
      const [lng, lat] = f.geometry.coordinates;
      if (f.properties.cluster) {
        const count = f.properties.point_count || f.properties.total || 0;
        const colorGroups = f.properties.countByColor || {};
        return {
          id: `cluster_${f.id}`,
          center: [lat, lng] as [number, number],
          count,
          colorGroups,
          isSingle: false,
        };
      } else {
        const fid = f.properties.__id;
        const marker = id2MarkerRef.current.get(fid);
        return {
          id: marker?.id ?? fid,
          center: (marker?.position ?? [lat, lng]) as [number, number],
          markers: marker ? [marker] : [],
          colorGroups: { [marker?.tint || "#ffffff"]: 1 },
          isSingle: true,
        };
      }
    });

    const nonClusterableSingles: ClusterData[] = nonClusterableMarkers
      .filter((marker) => {
        if (!marker?.position?.[0] || !marker?.position?.[1]) return false;
        const [lat, lng] = marker.position;
        return b.contains(L.latLng(lat, lng));
      })
      .map((marker) => ({
        id: marker.id,
        center: marker.position,
        markers: [marker],
        colorGroups: { [marker.tint || "#ffffff"]: 1 },
        isSingle: true,
      }));

    setClusters([...clustered, ...nonClusterableSingles]);
  }, [map, enableClustering, MIN_ZOOM_CLUSTER, maxClusterRadius, markers]);

  const updateMarkersVisible = useCallback(() => {
    if (!map || map.getZoom() < MIN_ZOOM_CLUSTER) return;
    const b = map.getBounds();

    const visible = markers.filter((marker) => {
      if (!marker?.position?.[0] || !marker?.position?.[1]) return false;
      const [lat, lng] = marker.position;
      return b.contains(L.latLng(lat, lng));
    });
    setMarkersVisible(visible);
  }, [map, markers, MIN_ZOOM_CLUSTER]);

  useEffect(() => {
    if (!map) return;
    updateClusters();
    updateMarkersVisible();
    const handler = () => {
      updateClusters();
    };
    map.on("moveend", updateMarkersVisible);
    map.on("zoomend moveend", handler);
    return () => {
      map.off("zoomend moveend", handler);
      map.off("moveend", updateMarkersVisible);
    };
  }, [map, updateClusters]);

  useEffect(() => {
    if (!map) return;

    const container = new PIXI.Container();
    const overlay = (L as any).pixiOverlay(
      (utils: any) => {
        const scale = utils.getScale();
        const renderer = utils.getRenderer();
        const container = utils.getContainer();
        renderer.reset();

        container.children.forEach((c: any) => {
          const RADIUS_PX = c.isCluster
            ? 0.4 +
              (parseFloat(config?.configuracoes?.ftth?.clusterIconSize) || 5) /
                20
            : 0.3 +
              (parseFloat(config?.configuracoes?.ftth?.onuIcon) || 5) / 20;
          const invScale = 1 / scale;
          c.scale.set(RADIUS_PX * invScale);
        });

        renderer.render(container);
      },
      container,
      { destroyInteractionManager: false },
    );

    overlay.addTo(map);
    setPixiOverlay(overlay);
    return () => overlay.remove();
  }, [map, config]);

  useEffect(() => {
    if (!map) return;
    if (pixiOverlay) {
      pixiOverlay.remove();
      setPixiOverlay(null);
    }
    const container = new PIXI.Container();
    const overlay = (L as any).pixiOverlay(
      (utils: any) => {
        const scale = utils.getScale();
        const renderer = utils.getRenderer();
        const container = utils.getContainer();
        renderer.reset();

        container.children.forEach((c: any) => {
          const RADIUS_PX = c.isCluster
            ? 0.4 +
              (parseFloat(config?.configuracoes?.ftth?.clusterIconSize) || 5) /
                20
            : 0.3 +
              (parseFloat(config?.configuracoes?.ftth?.onuIcon) || 5) / 20;
          const invScale = 1 / scale;
          c?.scale?.set(RADIUS_PX * invScale);
        });

        renderer.render(container);
      },
      container,
      { destroyInteractionManager: false },
    );

    overlay.addTo(map);
    setPixiOverlay(overlay);

    return () => {
      if (overlay) {
        overlay.remove();
        container.destroy({ children: true });
      }
    };
  }, [map]);

  useEffect(() => {
    if (!pixiOverlay || !texturesLoaded) return;

    const { utils } = pixiOverlay;
    const container = utils.getContainer();
    const project = utils.latLngToLayerPoint;
    const scale = utils.getScale();

    spriteByIdRef.current.clear();
    container.removeChildren();

    drawBackground(container);

    if (enableClustering && map.getZoom() <= MIN_ZOOM_CLUSTER) {
      clusters.forEach((cluster) => {
        if (cluster.isSingle) {
          const marker = cluster.markers![0];
          drawMarker(marker, container, project, scale);
          return;
        }
        drawCluster(cluster, container, project);
      });
    } else {
      markersVisible.forEach((m) => {
        drawMarker(m, container, project);
      });
    }

    if (loadingDetails) {
      setTimeout(() => {
        setLoadingDetails(false);
      }, 100);
    }
  }, [
    pixiOverlay,
    texturesLoaded,
    colorMode,
    markersVisible,
    clusters,
    enableClustering,
    map,
    elementSelected,
    elementONUSelected,
    showLabel,
  ]);

  const drawBackground = (container: PIXI.Container) => {
    const bg = new PIXI.Graphics();
    const size = 1e10;
    const hitArea = new PIXI.Rectangle(-size, -size, size * 2, size * 2);
    bg.hitArea = hitArea;
    bg.interactive = true;
    (bg as any).eventMode = "static";

    let pointerDownTime = 0;
    const CLICK_THRESHOLD = 250;

    bg.on("pointerdown", () => {
      pointerDownTime = Date.now();
    });

    bg.on("pointerup", (e: any) => {
      const duration = Date.now() - pointerDownTime;
      if (duration < CLICK_THRESHOLD) {
        e.stopPropagation();
        if (typeof onBackgroundClick === "function") {
          onBackgroundClick();
        }
      }
    });

    container.addChild(bg as any);
  };

  const drawMarker = (
    m: MarkerData,
    container: PIXI.Container,
    project: (latlng: L.LatLng) => L.Point,
    scale?: number,
  ) => {
    const texture = Assets.get(`marker_${m.iconId}`);
    const markerTexture = Assets.get(`${m.baseIcon}`);
    const markerTextureLight = Assets.get("marker_circle_light");

    if (!(texture && markerTexture)) return;

    const node = new PIXI.Container() as any;

    if (!(m?.position?.[0] && m?.position?.[1])) return;
    const point = project(L.latLng(...m?.position));
    node.position.set(point.x, point.y);

    node.markerId = m.id;

    const spriteMarker = new PIXI.Sprite(markerTexture);
    spriteMarker.tint = m.tint ? PIXI.utils.string2hex(m.tint) : 0xffffff;
    spriteMarker.anchor.set(0.5);

    const sprite = new PIXI.Sprite(texture);
    sprite.tint = colorMode === "light" ? 0x000000 : 0xffffff;
    sprite.anchor.set(0.5);
    (sprite as any).baseScale = sprite.scale.clone();

    node.addChild(spriteMarker);
    node.addChild(sprite);

    if (showLabel) {
      const label = new PIXI.BitmapText(m.label || m.titulo || "", {
        fontName: colorMode === "light" ? "labelFontBlack" : "labelFontWhite",
        align: "center",
      });

      label.anchor.set(0.5, 1);
      label.y = -55;

      node.addChild(label);
    }

    node.eventMode = "static";
    node.cursor = "pointer";

    node.on("pointerdown", (e: any) => {
      e.stopPropagation();
      onClick(m.id);
    });

    container.addChild(node);

    spriteByIdRef.current.set(m.id, { spriteMarker, sprite });

    if (m.alerts && m.alerts.length > 0) {
      const explosion = new PIXI.Sprite(markerTextureLight);
      explosion.tint = PIXI.utils.string2hex(m.tint || "#ffffff");
      explosion.anchor.set(0.5);
      explosion.scale.set(0.1);
      node.addChildAt(explosion, 0);

      explosionsRef.current.set(m.id, { graphic: explosion, t: 0 });
    } else {
      explosionsRef.current.delete(m.id);
    }
  };

  const drawCluster = (
    cluster: ClusterData,
    container: PIXI.Container,
    project: (latlng: L.LatLng) => L.Point,
  ) => {
    if (!(cluster?.center?.[0] && cluster?.center?.[1])) return;

    const point = project(L.latLng(...cluster.center));
    const node = new PIXI.Container() as any;
    node.position.set(point.x, point.y);
    node.isCluster = true;
    node.interactive = true;
    node.buttonMode = true;
    node.markerId = cluster.id;
    node.clusterData = cluster;

    const bgCircle = new PIXI.Graphics();
    bgCircle.beginFill(colorMode === "light" ? 0xffffff : 0x333333, 0.8);
    bgCircle.drawCircle(0, 0, 2);
    bgCircle.endFill();

    const colors = Object.entries(cluster.colorGroups || {}) as [
      string,
      number,
    ][];
    const normalizedColors = percentageNormalize(colors, 0.075);

    if (normalizedColors.length > 1) {
      drawPieChart(node, normalizedColors, colorMode);
    } else if (normalizedColors.length === 1) {
      const color = normalizedColors[0][0];
      const donut = new PIXI.Graphics();
      donut.beginFill(PIXI.utils.string2hex(color), 1);
      donut.drawCircle(0, 0, 16 * 2);
      donut.endFill();

      donut.beginFill(colorMode === "light" ? 0xffffff : 0x333333, 1);
      donut.drawCircle(0, 0, 12 * 2);
      donut.endFill();

      node.addChild(donut);
    }

    const total =
      (typeof cluster.count === "number" && cluster.count) ||
      (Array.isArray(cluster.markers) ? cluster.markers.length : 0) ||
      0;

    const formatCount = (n: number) => {
      if (n >= 1000) {
        const rounded = Math.round((n / 1000) * 10) / 10;
        return (rounded % 1 === 0 ? rounded.toFixed(0) : rounded) + "k";
      }
      return n.toLocaleString();
    };

    const countText = new PIXI.BitmapText(formatCount(total), {
      fontName: colorMode === "light" ? "countFontBlack" : "countFont",
      align: "center",
      fontSize: total < 1000 ? 20 : total < 10000 ? 16 : 14,
    });

    countText.anchor.set(0.5);

    node.addChild(bgCircle);
    node.addChild(countText);

    node.on("click", (e: any) => {
      e.stopPropagation();
      onClusterClick(cluster);
    });

    container.addChild(node);
  };

  const drawPieChart = (
    container: PIXI.Container,
    colors: [string, number][],
    colorMode: string,
  ) => {
    const donut = new PIXI.Graphics();
    let startAngle = -Math.PI / 2;

    colors.sort((a, b) => b[1] - a[1]);

    colors.forEach(([color, percentage]) => {
      const sliceAngle = percentage * 2 * Math.PI;
      donut.beginFill(PIXI.utils.string2hex(color), 1);
      donut.moveTo(0, 0);
      donut.arc(0, 0, 16 * 2, startAngle, startAngle + sliceAngle);
      donut.lineTo(0, 0);
      donut.endFill();
      startAngle += sliceAngle;
    });

    donut.beginFill(colorMode === "light" ? 0xffffff : 0x333333, 1);
    donut.drawCircle(0, 0, 12 * 2);
    donut.endFill();

    container.addChild(donut as any);
  };

  useEffect(() => {
    if (onClustersChange) {
      onClustersChange(clusters);
    }
  }, [clusters, onClustersChange]);

  useEffect(() => {
    const ticker = PIXI.Ticker.shared;
    const spritesRef = spriteByIdRef.current;
    const explosions = explosionsRef.current;

    const animatedIds = new Set(
      [
        elementSelected?.id ?? elementSelected?.tempId,
        elementONUSelected?.id ?? elementONUSelected?.tempId,
      ].filter(Boolean),
    );

    const globalTicker = () => {
      const t = ticker.lastTime;
      if (animatedIds.size > 0) {
        const scale = 1 + 0.16 * Math.sin(t / 200);
        for (const id of animatedIds) {
          const s = spritesRef.get(id);
          if (s) {
            s.sprite.scale.set(scale);
            s.spriteMarker.scale.set(scale);
          }
        }
      }

      if (configurationMap?.configuracoes?.animacao?.alertas != false) {
        const delta = ticker.deltaMS / 1000;
        for (const [, data] of explosions) {
          data.t += delta;
          const scaleFactor = 1 + data.t * 2;
          data.graphic.scale.set(scaleFactor);
          data.graphic.alpha = Math.max(0, 0.7 - data.t * 0.7);
          if (data.t > 1) {
            data.t = 0;
            data.graphic.scale.set(1);
            data.graphic.alpha = 0.6;
          }
        }
      }
    };
    ticker.add(globalTicker);
    return () => {
      ticker.remove(globalTicker);
    };
  }, [
    configurationMap?.configuracoes?.animacao?.alertas,
    elementSelected?.id,
    elementSelected?.tempId,
    elementONUSelected?.id,
    elementONUSelected?.tempId,
    focusNodeId,
    showLabel,
  ]);

  useEffect(() => {
    if (!pixiOverlay) return;
    const ticker = PIXI.Ticker.shared;

    if (tickerFantasmaRef.current) {
      try {
        ticker.remove(tickerFantasmaRef.current);
      } catch (e) {}
    }

    const tickerFantasma = () => {
      pixiOverlay.redraw?.();
    };

    tickerFantasmaRef.current = tickerFantasma;
    ticker.add(tickerFantasma);

    return () => {
      if (tickerFantasmaRef.current) {
        ticker.remove(tickerFantasmaRef.current);
        tickerFantasmaRef.current = null;
      }
    };
  }, [pixiOverlay]);

  return null;
}

function getDefaultIcon(): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
<path d="M12.8 32V20.7059H19.2V32H27.2V16.9412H32L16 0L0 16.9412H4.8V32H12.8Z" fill="white"/>
</svg>
`;
  const b64 = btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${b64}`;
}

function getClusterIcon(): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
    <circle cx="20" cy="20" r="20" fill="white"/>
  </svg>`;
  const b64 = btoa(unescape(encodeURIComponent(svg)));
  return `data:image/svg+xml;base64,${b64}`;
}
